import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { ArrowLeft, BookOpen, ExternalLink, Sparkles, Send, Languages, MessageSquarePlus, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { getPlaceArticle, getContributions, createContribution, API } from "@/lib/api";
import { T, useLanguage } from "@/context/LanguageContext";
import { InlineListen } from "@/components/site/ListenControl";
import { LanguagePicker } from "@/components/site/LanguagePicker";
import { useTTS } from "@/context/TTSContext";
import { copyText } from "@/lib/clipboard";
import { Seo } from "@/components/site/Seo";
import { canonicalUrl } from "@/lib/seo";

export default function ArticlePage() {
  const { geoId, module } = useParams();
  const { lang, dict } = useLanguage();
  const tts = useTTS();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [contribs, setContribs] = useState([]);

  useEffect(() => {
    setData(null); setError(false);
    window.scrollTo(0, 0);
    getPlaceArticle(geoId, module).then(setData).catch(() => setError(true));
    getContributions(geoId, module).then((d) => setContribs(d.contributions || [])).catch(() => {});
  }, [geoId, module]);

  if (error) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl text-navy"><T>Article not available</T></h1>
      <Link to="/explore/states" className="mt-4 inline-block text-oldglory-red"><T>Back to Explore</T></Link>
    </div>
  );

  if (!data) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="article-loading">
      <Sparkles className="mx-auto mb-4 h-8 w-8 animate-pulse text-oldglory-red" />
      <p className="font-display text-xl text-navy"><T>Composing this article…</T></p>
      <p className="mt-2 text-sm text-muted-foreground"><T>Generating original, sourced content for this place.</T></p>
    </div>
  );

  const paragraphs = data.paragraphs || [];
  const bodyHtml = data.body_html || "";
  const plainFromHtml = bodyHtml ? bodyHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "";
  const readableSrc = paragraphs.length ? paragraphs : (plainFromHtml ? [plainFromHtml] : []);
  const readable = readableSrc.map((p) => (lang.code === "en" ? p : dict[p] || p));
  const isReading = (txt) => !!tts?.activeText && tts.activeText === (lang.code === "en" ? txt : dict[txt] || txt);
  const HL = "rounded-md bg-oldglory-red/10 shadow-[inset_3px_0_0_0_#c8102e] px-2 -mx-2 transition-colors duration-300";
  const shareUrl = `${window.location.origin}/learn/${geoId}/${module}`;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="article-page">
      <Seo
        title={`${data.label} — ${data.place_name}`}
        path={`/learn/${geoId}/${module}`}
        type="article"
        description={data.snippet || plainFromHtml || paragraphs[0] || `${data.label} in ${data.place_name} — an original, sourced article on USAPulse.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${data.label} — ${data.place_name}`,
          about: data.place_name,
          url: canonicalUrl(`/learn/${geoId}/${module}`),
          publisher: { "@type": "Organization", name: "USAPulse.net" },
        }}
      />
      <Link to={`/place${data.url_path}`} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="article-back">
        <ArrowLeft className="h-4 w-4" /> <T>Back to</T> {data.place_name}
      </Link>

      <span className="mb-2 inline-block rounded bg-navy/5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-navy">{data.place_name}</span>
      <h1 className="font-display text-4xl font-black leading-tight text-navy md:text-5xl" data-testid="article-title"><T>{data.label}</T></h1>

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <InlineListen paragraphs={readable} label="Listen to this article" testid="article-listen" />
        <LanguagePicker
          showGroups
          trigger={
            <button className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy transition-all hover:border-oldglory-red hover:text-oldglory-red" data-testid="article-translate">
              <Languages className="h-4 w-4" /> <T>Translate</T> <span className="text-[11px] opacity-60">▾</span>
            </button>
          }
        />
        {data.source === "ai" && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-parchment px-3 py-1.5 text-[11px] font-semibold text-navy">
            <Sparkles className="h-3.5 w-3.5 text-oldglory-red" /> <T>AI-assisted overview</T>
          </span>
        )}
        {data.source === "cms" && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
            <BookOpen className="h-3.5 w-3.5" /> <T>Editor-published</T>{data.author ? ` · ${data.author}` : ""}
          </span>
        )}
      </div>

      {bodyHtml ? (
        <article className="prose-usapulse mt-8" data-testid="article-body-html"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml, { ADD_TAGS: ["iframe"], ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "target", "data-align"] }) }} />
      ) : (
        <article className="prose-usapulse mt-8 space-y-5">
          {paragraphs.map((p, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`text-lg leading-relaxed text-ink/85 ${isReading(p) ? HL : ""}`} data-testid={`article-para-${i}`}><T>{p}</T></motion.p>
          ))}
        </article>
      )}

      {data.facts?.length > 0 && (
        <div className="mt-10 rounded-xl border border-border bg-parchment/50 p-5" data-testid="article-facts">
          <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-navy">
            <Sparkles className="h-5 w-5 text-oldglory-red" /> <T>Did You Know?</T>
          </h2>
          <ul className="space-y-2">
            {data.facts.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm text-ink/85">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-oldglory-red" /> <span><T>{f.replace(/^\s*did you know\??\s*[:\-]?\s*/i, "")}</T></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border pt-5 text-sm">
        <span className="font-semibold text-navy"><T>Sources</T>:</span>
        {data.wikipedia?.available && (
          <a href={data.wikipedia.wikipedia_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-navy hover:text-oldglory-red" data-testid="article-wikipedia">
            <BookOpen className="h-4 w-4" /> Wikipedia <ExternalLink className="h-3 w-3 opacity-60" />
          </a>
        )}
        <a href="https://www.census.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-navy hover:text-oldglory-red">U.S. Census Bureau <ExternalLink className="h-3 w-3 opacity-60" /></a>
        {(data.sources || []).map((s, i) => s.label && (
          <span key={i} className="text-muted-foreground">· {s.label}{s.hint ? ` (${s.hint})` : ""}</span>
        ))}
      </div>

      <ShareCard title={data.label} placeName={data.place_name} shareUrl={shareUrl} docketId={data.docket_id} />

      <ModuleContribute geoId={geoId} module={module} label={data.label} placeName={data.place_name} contribs={contribs} setContribs={setContribs} />
    </div>
  );
}

function ShareCard({ title, placeName, shareUrl, docketId }) {
  const [copied, setCopied] = useState(false);
  const text = `${title} — ${placeName} · USAPulse`;
  const copy = async () => { const ok = await copyText(shareUrl); setCopied(ok); toast[ok ? "success" : "error"](ok ? "Link copied" : "Copy failed"); if (ok) setTimeout(() => setCopied(false), 1800); };
  const enc = encodeURIComponent;
  const socials = [
    { name: "X", url: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}` },
    { name: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { name: "LinkedIn", url: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}` },
    { name: "WhatsApp", url: `https://wa.me/?text=${enc(text + " " + shareUrl)}` },
  ];
  return (
    <section className="mt-10 rounded-2xl border border-border bg-navy p-6 text-white" data-testid="share-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Share2 className="h-6 w-6 text-white/80" />
          <div>
            <h3 className="font-display text-lg font-bold"><T>Share this article</T></h3>
            <p className="text-sm text-white/60"><T>Help your community discover</T> {placeName}.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {socials.map((s) => (
            <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white/10 px-3.5 py-2 text-sm font-semibold transition-colors hover:bg-white/20" data-testid={`share-${s.name.toLowerCase()}`}>{s.name}</a>
          ))}
          <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-full bg-oldglory-red px-3.5 py-2 text-sm font-semibold transition-colors hover:bg-oldglory-dark" data-testid="share-copy">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} <T>{copied ? "Copied" : "Copy link"}</T>
          </button>
        </div>
      </div>
      {docketId && (
        <p className="mt-3 border-t border-white/10 pt-3 text-xs text-white/50">
          <T>Portable docket API for partner Pulse sites</T>: <a href={`${API}/docket/${docketId}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">{API}/docket/{docketId}</a>
        </p>
      )}
    </section>
  );
}

function ModuleContribute({ geoId, module, label, placeName, contribs, setContribs }) {
  const { languages, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", author_name: "" });
  const [language, setLanguage] = useState(lang.code);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim() || !form.author_name.trim()) {
      toast.error("Please add a title, your note and your name.");
      return;
    }
    setBusy(true);
    try {
      const created = await createContribution({ geo_id: geoId, module, language, ...form });
      setContribs((p) => [created, ...p]);
      setForm({ title: "", content: "", author_name: "" });
      setOpen(false);
      toast.success("Thank you! Your contribution helps expand this page.");
    } catch {
      toast.error("Could not submit right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const ip = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-navy";

  return (
    <section className="mt-12 rounded-2xl border border-dashed border-navy/20 bg-navy/[0.03] p-6" data-testid="module-contribute">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-navy"><T>Help expand</T> "{label}"</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            <T>Know something about</T> {label.toLowerCase()} <T>in</T> {placeName}? <T>Share facts, corrections or local knowledge — in any language.</T>
          </p>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 rounded-full bg-oldglory-red px-4 py-2 text-sm font-semibold text-white hover:bg-oldglory-dark" data-testid="module-contribute-toggle">
          <MessageSquarePlus className="h-4 w-4" /> <T>{open ? "Close" : "Suggest information"}</T>
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="module-contribute-form">
          <input value={form.author_name} onChange={(e) => setForm((f) => ({ ...f, author_name: e.target.value }))} placeholder="Your name" className={ip} data-testid="mc-author" />
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title / summary" className={ip} data-testid="mc-title" />
          <textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} rows={4} placeholder="What should we add?" className={ip} data-testid="mc-content" />
          <div className="flex items-center justify-between gap-3">
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className={ip + " max-w-[220px]"} data-testid="mc-language">
              {languages.map((l) => <option key={l.code} value={l.code}>{`${l.name} — ${l.native_name}`}</option>)}
            </select>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-60" data-testid="mc-submit">
              <Send className="h-4 w-4" /> <T>{busy ? "Sending…" : "Submit"}</T>
            </button>
          </div>
        </form>
      )}

      {contribs.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="module-contribution-list">
          {contribs.map((c) => (
            <div key={c.id} className="rounded-lg border border-border bg-white p-4" data-testid={`mc-item-${c.id}`}>
              <p className="font-semibold text-navy">{c.title}</p>
              <p className="mt-1 whitespace-pre-line text-sm text-ink/85">{c.content}</p>
              <p className="mt-2 text-xs text-muted-foreground">— {c.author_name}{c.author_role ? `, ${c.author_role}` : ""}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
